import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { AlertCircle, BookOpen, Calendar, CheckCircle2, ClipboardCheck, ClipboardList, FileSpreadsheet, FileText, Info, ListChecks, Ruler } from 'lucide-react';
import { useState } from 'react';
import { SourceTemplateCard } from './SourceTemplateCard';
import { useVolumeMetadata } from './useVolumeMetadata';
/**
 * Shared header + metadata panel used by every workspace kind.
 *
 * Surfaces everything we know about a volume:
 *   - Title + breadcrumb-style kind label
 *   - Header chip strip: file format, required/optional, due date, page limit
 *   - Content summary (one-liner from the insights pipeline)
 *   - "About this volume" expandable card with content requirements,
 *     file requirements, format rules
 *   - Source templates list (latest version of each, amendment-aware)
 *
 * The data join happens in `useVolumeMetadata`, which is a pure derivation
 * over the existing `useSolicitationInsights` cache — no extra RPCs.
 */ export function VolumeMetadataPanel({ projectId, volume }) {
    const meta = useVolumeMetadata(projectId, volume);
    const refs = volume.sourceAttachmentRefs ?? [];
    const sourceKind = volume.kind === 'workbook' ? 'workbook' : 'template';
    // PR-B.8.0g: source-driven volume composition.
    // `volume.requiredItems` is the literal "this volume shall contain: a/b/c"
    // enumeration the LLM extracted, with per-bullet source grounding.
    // `volume.attachmentAssignments` is the explicit list of (attachment label
    // → this volume) mappings. Both come from `extractor_volume_structure.go`.
    // Empty arrays here mean the source never explicitly populated them — the
    // resolver and UI fail closed (no guessed bullets, no guessed attachments).
    const requiredItems = volume.requiredItems ?? [];
    const attachmentAssignments = volume.attachmentAssignments ?? [];
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsx(Header, {
                volume: volume,
                meta: meta
            }),
            meta.pipelineVolume?.contentSummary && /*#__PURE__*/ _jsx(Card, {
                icon: /*#__PURE__*/ _jsx(Info, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                title: "Purpose",
                children: /*#__PURE__*/ _jsx("p", {
                    className: "text-sm leading-relaxed text-slate-700",
                    children: meta.pipelineVolume.contentSummary
                })
            }),
            requiredItems.length > 0 && /*#__PURE__*/ _jsx(RequiredItemsCard, {
                items: requiredItems
            }),
            /*#__PURE__*/ _jsx(SubmissionInstructions, {
                volume: volume,
                meta: meta,
                hasSourceTemplate: refs.length > 0
            }),
            refs.length > 0 ? /*#__PURE__*/ _jsx(Card, {
                icon: /*#__PURE__*/ _jsx(FileText, {
                    className: "h-4 w-4 text-slate-500",
                    "aria-hidden": "true"
                }),
                title: refs.length === 1 ? 'Source template' : `Source templates (${refs.length})`,
                subtitle: "Latest version across amendments — download, fill, and re-upload",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "space-y-2",
                    children: refs.map((ref)=>/*#__PURE__*/ _jsx(SourceTemplateCard, {
                            attachment: ref,
                            kind: sourceKind
                        }, ref.attachmentId))
                })
            }) : volume.kind !== 'prose' ? attachmentAssignments.length === 0 ? /*#__PURE__*/ _jsx(NoStandaloneTemplatesCallout, {}) : /*#__PURE__*/ _jsx(NoSourceCallout, {
                volume: volume
            }) : null,
            /*#__PURE__*/ _jsx(DetailsCard, {
                meta: meta
            })
        ]
    });
}
// ---------------------------------------------------------------------------
// Required items card — the user's "what does this volume contain" checklist
// ---------------------------------------------------------------------------
/**
 * RequiredItemsCard renders the literal "this volume shall contain: a/b/c"
 * enumeration the volume_structure extractor pulled from the source. Each
 * item is one numbered row with:
 *   - the bullet label (extracted verbatim from the source, max ~120 chars)
 *   - an optional reference (section number, attachment label, etc.) shown
 *     as muted text after the label
 *   - a "required" / "optional" badge derived from the source language
 *     ("shall"/"must" → required; "may"/"if any" → optional)
 *   - a "Show source" expander that reveals the full verbatim quote from
 *     the solicitation block this item came from
 *
 * This is the user's grounded submission checklist. Unlike SubmissionInstructions
 * (which is a synthesized narrative), every line here maps directly to a
 * sentence in the source document — no LLM rephrasing in the user-visible text.
 */ function RequiredItemsCard({ items }) {
    return /*#__PURE__*/ _jsx(Card, {
        icon: /*#__PURE__*/ _jsx(ListChecks, {
            className: "h-4 w-4 text-slate-500",
            "aria-hidden": "true"
        }),
        title: "What this volume must contain",
        subtitle: `${items.length} item${items.length === 1 ? '' : 's'} extracted from the solicitation`,
        children: /*#__PURE__*/ _jsx("ol", {
            className: "space-y-2.5",
            children: items.map((item, i)=>/*#__PURE__*/ _jsxs("li", {
                    className: "flex items-start gap-2 text-sm",
                    children: [
                        /*#__PURE__*/ _jsx("span", {
                            className: "mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-600",
                            children: i + 1
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "min-w-0 flex-1",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex flex-wrap items-baseline gap-1.5",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "leading-relaxed text-slate-800",
                                            children: item.label
                                        }),
                                        item.reference && /*#__PURE__*/ _jsxs("span", {
                                            className: "text-[11px] text-slate-500",
                                            children: [
                                                "· ",
                                                item.reference
                                            ]
                                        }),
                                        !item.required && /*#__PURE__*/ _jsx("span", {
                                            className: "text-[10px] uppercase tracking-wide text-slate-400",
                                            children: "optional"
                                        })
                                    ]
                                }),
                                item.sourceText && item.sourceText.trim().length > 20 && /*#__PURE__*/ _jsx(SourceQuote, {
                                    text: item.sourceText
                                })
                            ]
                        })
                    ]
                }, `${i}-${item.label}`))
        })
    });
}
// ---------------------------------------------------------------------------
// "No standalone templates" callout — the Volume VI / Reps & Certs case
// ---------------------------------------------------------------------------
/**
 * NoStandaloneTemplatesCallout is shown when a non-prose volume has zero
 * attachmentAssignments. This happens for volumes whose content lives
 * INSIDE the main solicitation document — the canonical example is
 * Volume VI ("Solicitation, Offer and Award Documents and Certifications/
 * Representations"), where the user fills out the SF1449 (Section A) and
 * Section K Reps & Certs in the main solicitation PDF itself, not in a
 * separate template file.
 *
 * Before PR-B.8.0f the legacy keyword resolver would auto-link the entire
 * solicitation PDF and any cert/rep file by filename keyword, producing
 * the "wrong attachments showing up on Volume VI" bug. The new behavior
 * fails closed and shows this explicit explanation instead.
 */ function NoStandaloneTemplatesCallout() {
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-xl border border-slate-200 bg-slate-50/60 p-4 text-sm text-slate-700",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-start gap-2",
            children: [
                /*#__PURE__*/ _jsx(Info, {
                    className: "mt-0.5 h-4 w-4 flex-shrink-0 text-slate-500",
                    "aria-hidden": "true"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "font-medium text-slate-800",
                            children: "No standalone templates"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1 text-xs leading-relaxed text-slate-600",
                            children: [
                                "The content for this volume lives inside the main solicitation document — open the latest amendment under ",
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold",
                                    children: "Sources / Files"
                                }),
                                " and fill out the sections listed in the checklist above."
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
// ---------------------------------------------------------------------------
// Submission Instructions — synthesized "how to submit" checklist
// ---------------------------------------------------------------------------
/**
 * SubmissionInstructions renders an actionable, narrative checklist for the
 * volume the user is currently looking at. Synthesizes:
 *   - file format / page limit (per_instance vs per_volume) → "Submit X
 *     pages per Y instance, max Z instances" or "Submit one PDF, ≤N pages"
 *   - due date (when set on the volume itself, not the project default)
 *   - source attachment (when one was auto-linked or graph-resolved)
 *   - content requirements (each rendered as its own numbered step)
 *
 * The intent: a proposal team member opens Volume I and sees, in order:
 *   1. Submit a Case Study Submission as a PDF
 *   2. Limit each case study to 3 pages × 4 case studies max
 *   3. Include client organization name, point of contact, period of perf
 *   4. Submit electronically via GSA eBuy by February 3, 2020
 *
 * — instead of a flat bullet list of "extracted requirements" that doesn't
 * read like instructions.
 *
 * Returns null when there's nothing to render (no content reqs + no page
 * limit + no due date) so the panel doesn't show an empty checklist.
 */ function SubmissionInstructions({ volume, meta, hasSourceTemplate }) {
    const pageLimit = meta.pageLimits.find((p)=>p.isCurrentVersion) ?? meta.pageLimits[0];
    const fileReq = meta.fileRequirements.find((f)=>f.isCurrentVersion) ?? meta.fileRequirements[0];
    const contentReqs = meta.contentRequirements;
    const steps = [];
    // Step 1 — what kind of file to produce
    const fmt = (volume.fileFormat || fileReq?.fileFormat || '').toUpperCase();
    if (fmt) {
        if (hasSourceTemplate) {
            steps.push({
                kind: 'format',
                text: `Download the source template below, fill it in, and re-upload as ${fmt}.`
            });
        } else if (volume.kind === 'workbook') {
            steps.push({
                kind: 'format',
                text: `Submit as a ${fmt} spreadsheet.`
            });
        } else if (volume.kind === 'template') {
            steps.push({
                kind: 'format',
                text: `Complete and submit as a ${fmt} document.`
            });
        } else {
            steps.push({
                kind: 'format',
                text: `Submit as a ${fmt} document.`
            });
        }
    }
    // Step 2 — page / instance / duration limit (the bit that surprised the
    // user with "3 pages" before PR-B.7.3)
    if (pageLimit) {
        let text = '';
        const unit = pageLimit.unit || 'pages';
        if (pageLimit.scope === 'per_instance' && pageLimit.instanceLabel) {
            const labelSingular = pluralize(pageLimit.instanceLabel, 1);
            if (pageLimit.instanceMax && pageLimit.instanceMax > 0) {
                const labelPlural = pluralize(pageLimit.instanceLabel, pageLimit.instanceMax);
                text = `Limit each ${labelSingular} to ${pageLimit.limit} ${unit}, with no more than ${pageLimit.instanceMax} ${labelPlural} total.`;
            } else {
                text = `Limit each ${labelSingular} to ${pageLimit.limit} ${unit}.`;
            }
        } else if (pageLimit.scope === 'per_subsection' && pageLimit.subsectionScope && pageLimit.subsectionScope.toLowerCase() !== 'other') {
            text = `Limit each ${pageLimit.subsectionScope} to ${pageLimit.limit} ${unit}${pageLimit.instanceMax && pageLimit.instanceMax > 0 ? `, max ${pageLimit.instanceMax}` : ''}.`;
        } else {
            text = `The volume must not exceed ${pageLimit.limit} ${unit}.`;
        }
        if (pageLimit.exclusions && pageLimit.exclusions.length > 0) {
            text += ` (${pageLimit.exclusions.join(', ')} ${pageLimit.exclusions.length === 1 ? "doesn't" : "don't"} count toward the limit.)`;
        }
        steps.push({
            kind: 'limit',
            text,
            source: pageLimit.sourceText
        });
    }
    // Steps 3..N — content requirements as individual must-include items.
    // PR-B.7.8c: don't double-prefix ("Include: Include client...") when the
    // extracted description already starts with "Include". Also stop hard-
    // capping the description at 240 chars — the user wants more detail
    // here, not less. We pass the source text along so the renderer can
    // surface the verbatim quote on demand for grounding.
    // One sentence can ground several requirements ("A Cover Page, Table of
    // Contents and/or a glossary" is three), so the step is keyed on its text.
    const seenContent = new Set();
    for (const c of contentReqs){
        const rawDesc = c.description || c.contentType?.replace(/_/g, ' ') || '';
        if (!rawDesc) continue;
        const desc = rawDesc.trim();
        // Strip leading "Include " / "Includes " / "Including " so we don't
        // render "Include: Include client organization name…".
        const stripped = desc.replace(/^(includes?|including)\s+/i, '');
        const dedupeKey = stripped.toLowerCase();
        if (seenContent.has(dedupeKey)) continue;
        seenContent.add(dedupeKey);
        steps.push({
            kind: 'content',
            text: `Include: ${stripped}`,
            source: c.sourceText
        });
    }
    // Final step — when does it have to be submitted by (volume-specific date
    // overrides the project-level proposal due date)
    if (volume.dueDate) {
        steps.push({
            kind: 'submit',
            text: `Submit by ${volume.dueDate}.`
        });
    }
    if (steps.length === 0) return null;
    return /*#__PURE__*/ _jsxs(Card, {
        icon: /*#__PURE__*/ _jsx(ClipboardCheck, {
            className: "h-4 w-4 text-slate-500",
            "aria-hidden": "true"
        }),
        title: "How to submit this volume",
        subtitle: `${steps.length} step${steps.length === 1 ? '' : 's'} synthesized from solicitation`,
        children: [
            /*#__PURE__*/ _jsx("ol", {
                className: "space-y-2",
                children: steps.map((step, i)=>/*#__PURE__*/ _jsxs("li", {
                        className: "flex items-start gap-2 text-sm",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: "mt-0.5 inline-flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-50 text-[10px] font-semibold text-slate-600",
                                children: i + 1
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "min-w-0 flex-1",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "leading-relaxed text-slate-800",
                                        children: step.text
                                    }),
                                    step.source && step.source.trim().length > 30 && /*#__PURE__*/ _jsx(SourceQuote, {
                                        text: step.source
                                    })
                                ]
                            })
                        ]
                    }, i))
            }),
            contentReqs.length > 0 && /*#__PURE__*/ _jsxs("p", {
                className: "mt-3 flex items-start gap-1.5 text-[11px] text-slate-500",
                children: [
                    /*#__PURE__*/ _jsx(ListChecks, {
                        className: "mt-0.5 h-3 w-3 flex-shrink-0",
                        "aria-hidden": "true"
                    }),
                    "Synthesized from ",
                    contentReqs.length,
                    " extracted content requirement",
                    contentReqs.length === 1 ? '' : 's',
                    " for this volume."
                ]
            })
        ]
    });
}
/**
 * SourceQuote shows a single verbatim quote from the solicitation under a
 * checklist step. Collapsed by default — when the quote is long the user
 * sees a "Show source" affordance and clicks to expand. Italicized,
 * indented, slate text. The point: the synthesized step text reads like
 * an instruction, and the source is one click away for grounding.
 */ function SourceQuote({ text }) {
    const [open, setOpen] = useState(false);
    const trimmed = text.trim();
    const preview = trimmed.length > 180 ? `${trimmed.slice(0, 177)}…` : trimmed;
    const showToggle = trimmed.length > 180;
    return /*#__PURE__*/ _jsxs("div", {
        className: "mt-1.5 rounded-md border-l-2 border-slate-200 bg-slate-50/60 px-2.5 py-1.5",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "text-[11px] italic leading-relaxed text-slate-600",
                children: [
                    "“",
                    open || !showToggle ? trimmed : preview,
                    "”"
                ]
            }),
            showToggle && /*#__PURE__*/ _jsx("button", {
                type: "button",
                onClick: ()=>setOpen((v)=>!v),
                className: "mt-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 hover:text-slate-700",
                children: open ? 'Hide source' : 'Show full source'
            })
        ]
    });
}
/**
 * pluralize handles the small set of irregular plurals we hit in
 * synthesized instructions ("case study" → "case studies", not
 * "case studys"). Falls back to a naive +s for unknown words.
 *
 * Only handles the patterns we've actually seen in extracted page-limit
 * instance labels — no point pulling in a full pluralization library
 * for what is effectively a closed vocabulary.
 */ function pluralize(word, count) {
    if (count === 1) return word;
    const lower = word.toLowerCase();
    // Words ending in consonant + y → -ies (case study, country, agency).
    // Don't apply when preceded by a vowel (boy, day).
    if (/[bcdfghjklmnpqrstvwxz]y$/.test(lower)) {
        return `${word.slice(0, -1)}ies`;
    }
    // Words ending in -s, -x, -z, -ch, -sh → -es (process, box, branch).
    if (/(s|x|z|ch|sh)$/.test(lower)) {
        return `${word}es`;
    }
    return `${word}s`;
}
// ---------------------------------------------------------------------------
// Header
// ---------------------------------------------------------------------------
function Header({ volume, meta }) {
    const KindIcon = volume.kind === 'workbook' ? FileSpreadsheet : volume.kind === 'template' ? ClipboardList : BookOpen;
    const kindLabel = volume.kind === 'workbook' ? 'Workbook' : volume.kind === 'template' ? 'Fillable template' : 'Document';
    // Page-limit chip — prefer the current-version limit when one is marked.
    // For volumes with multiple limit shapes (e.g. base + amendment 30 pages
    // vs Case Study per_instance 3pp×4) we just pick the first; the rest
    // surface inside the DetailsCard.
    const pageLimit = meta.pageLimits.find((p)=>p.isCurrentVersion) ?? meta.pageLimits[0];
    return /*#__PURE__*/ _jsxs("header", {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-wrap items-center gap-2 text-[11px] font-medium uppercase tracking-wide text-slate-500",
                children: [
                    /*#__PURE__*/ _jsx(KindIcon, {
                        className: "h-3.5 w-3.5",
                        "aria-hidden": "true"
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        children: kindLabel
                    }),
                    volume.fileFormat && /*#__PURE__*/ _jsx(Chip, {
                        variant: formatVariant(volume.fileFormat),
                        children: volume.fileFormat.toUpperCase()
                    }),
                    volume.requiredOptional && /*#__PURE__*/ _jsx(Chip, {
                        variant: volume.requiredOptional.toLowerCase() === 'required' ? 'required' : 'optional',
                        className: "capitalize",
                        children: volume.requiredOptional
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("h1", {
                className: "mt-0.5 truncate text-xl font-semibold text-slate-900 sm:text-2xl",
                children: volume.displayName
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "mt-1.5 flex flex-wrap items-center gap-2 text-xs text-slate-600",
                children: [
                    volume.dueDate && /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1",
                        children: [
                            /*#__PURE__*/ _jsx(Calendar, {
                                className: "h-3.5 w-3.5 text-slate-400",
                                "aria-hidden": "true"
                            }),
                            "Due ",
                            volume.dueDate
                        ]
                    }),
                    pageLimit && /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-amber-800",
                        children: [
                            /*#__PURE__*/ _jsx(Ruler, {
                                className: "h-3 w-3",
                                "aria-hidden": "true"
                            }),
                            formatPageLimitLabel(pageLimit),
                            pageLimit.exclusions && pageLimit.exclusions.length > 0 && /*#__PURE__*/ _jsxs("span", {
                                className: "text-amber-700/70",
                                children: [
                                    "· excl. ",
                                    pageLimit.exclusions[0]
                                ]
                            })
                        ]
                    }),
                    meta.formatRules.length > 0 && /*#__PURE__*/ _jsxs("span", {
                        className: "inline-flex items-center gap-1 text-slate-500",
                        children: [
                            meta.formatRules.length,
                            " format rule",
                            meta.formatRules.length === 1 ? '' : 's'
                        ]
                    })
                ]
            })
        ]
    });
}
/**
 * formatPageLimitLabel renders the volume header chip with scope-aware
 * shape. Three real-world cases the data layer captures:
 *
 *   per_volume    → "30 pages"
 *   per_instance  → "3 pages × 4 case studies max"
 *                   (or just "3 pages per case study" when instanceMax=0)
 *   per_subsection → "2 pages each (resume) · max 8"
 *
 * The previous chip lost the per_instance shape entirely — it just rendered
 * "3 pages" for Volume I's case study limit, which was wrong because the
 * proposal team can submit up to 4 case studies × 3 pages each.
 */ function formatPageLimitLabel(p) {
    const unit = p.unit || 'pages';
    const base = `${p.limit} ${unit}`;
    if (p.scope === 'per_instance' && p.instanceLabel) {
        if (p.instanceMax && p.instanceMax > 0) {
            // pluralize handles "case study" → "case studies" (not "case studys")
            return `${base} × ${p.instanceMax} ${pluralize(p.instanceLabel, p.instanceMax)} max`;
        }
        return `${base} per ${p.instanceLabel}`;
    }
    if (p.scope === 'per_subsection' && p.subsectionScope) {
        if (p.instanceMax && p.instanceMax > 0) {
            return `${base} per ${p.subsectionScope} · max ${p.instanceMax}`;
        }
        return `${base} per ${p.subsectionScope}`;
    }
    return base;
}
// ---------------------------------------------------------------------------
// Details card — collapsible groups for content/file/format requirements
// ---------------------------------------------------------------------------
function DetailsCard({ meta }) {
    const [open, setOpen] = useState(true);
    const totalDetails = meta.contentRequirements.length + meta.fileRequirements.length + meta.formatRules.length;
    if (totalDetails === 0) return null;
    return /*#__PURE__*/ _jsx(Card, {
        icon: /*#__PURE__*/ _jsx(ClipboardList, {
            className: "h-4 w-4 text-slate-500",
            "aria-hidden": "true"
        }),
        title: "Formatting and file requirements",
        subtitle: `${totalDetails} requirement${totalDetails === 1 ? '' : 's'} extracted from the solicitation`,
        collapsible: true,
        open: open,
        onToggle: ()=>setOpen((v)=>!v),
        children: /*#__PURE__*/ _jsxs("div", {
            className: "space-y-4",
            children: [
                meta.contentRequirements.length > 0 && /*#__PURE__*/ _jsx(Group, {
                    title: "Content requirements",
                    children: /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1.5",
                        children: meta.contentRequirements.map((c, i)=>{
                            // The LLM tags most content requirements as the catchall
                            // "other" type, so the contentType label is meaningless on
                            // its own. When that happens, hoist the description into
                            // the title slot and skip the redundant body line.
                            const isGenericType = !c.contentType || c.contentType.toLowerCase() === 'other' || c.contentType.toLowerCase() === 'unspecified';
                            const title = isGenericType ? c.description || 'Requirement' : c.contentType.replace(/_/g, ' ');
                            const showBody = !isGenericType && !!c.description;
                            return /*#__PURE__*/ _jsxs("li", {
                                className: "flex items-start gap-2 text-sm",
                                children: [
                                    c.required ? /*#__PURE__*/ _jsx(CheckCircle2, {
                                        className: "mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-emerald-600",
                                        "aria-hidden": "true"
                                    }) : /*#__PURE__*/ _jsx(Info, {
                                        className: "mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-slate-400",
                                        "aria-hidden": "true"
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: `text-slate-800 ${isGenericType ? '' : 'font-medium capitalize'}`,
                                                children: title
                                            }),
                                            !c.required && /*#__PURE__*/ _jsx("span", {
                                                className: "ml-1 text-[11px] uppercase tracking-wide text-slate-400",
                                                children: "optional"
                                            }),
                                            showBody && /*#__PURE__*/ _jsx("div", {
                                                className: "mt-0.5 text-xs text-slate-600",
                                                children: c.description
                                            })
                                        ]
                                    })
                                ]
                            }, `${c.contentType}-${i}`);
                        })
                    })
                }),
                meta.fileRequirements.length > 0 && /*#__PURE__*/ _jsx(Group, {
                    title: "File requirements",
                    children: /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1.5",
                        children: meta.fileRequirements.map((f, i)=>/*#__PURE__*/ _jsxs("li", {
                                className: "space-y-0.5 text-sm text-slate-700",
                                children: [
                                    f.fileFormat && /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-500",
                                                children: "Format:"
                                            }),
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-medium text-slate-800",
                                                children: f.fileFormat
                                            })
                                        ]
                                    }),
                                    f.namingConvention && /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-500",
                                                children: "Naming:"
                                            }),
                                            ' ',
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "font-mono text-xs text-slate-800",
                                                children: f.namingConvention
                                            })
                                        ]
                                    }),
                                    f.maxFileSizeMb != null && f.maxFileSizeMb > 0 && /*#__PURE__*/ _jsxs("div", {
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-slate-500",
                                                children: "Max size:"
                                            }),
                                            ' ',
                                            /*#__PURE__*/ _jsxs("span", {
                                                className: "font-medium text-slate-800",
                                                children: [
                                                    f.maxFileSizeMb,
                                                    " MB"
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }, i))
                    })
                }),
                meta.formatRules.length > 0 && /*#__PURE__*/ _jsx(Group, {
                    title: "Format rules",
                    children: /*#__PURE__*/ _jsx("ul", {
                        className: "space-y-1 text-sm",
                        children: meta.formatRules.map((r, i)=>/*#__PURE__*/ _jsxs("li", {
                                className: "text-slate-700",
                                children: [
                                    /*#__PURE__*/ _jsxs("span", {
                                        className: "text-slate-500 capitalize",
                                        children: [
                                            r.ruleType.replace(/_/g, ' '),
                                            ":"
                                        ]
                                    }),
                                    ' ',
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "text-slate-800",
                                        children: r.value
                                    }),
                                    r.scope && /*#__PURE__*/ _jsxs("span", {
                                        className: "ml-1 text-[11px] text-slate-500",
                                        children: [
                                            "(",
                                            r.scope,
                                            ")"
                                        ]
                                    })
                                ]
                            }, `${r.ruleType}-${i}`))
                    })
                })
            ]
        })
    });
}
// ---------------------------------------------------------------------------
// Tiny shared primitives
// ---------------------------------------------------------------------------
function Card({ icon, title, subtitle, children, collapsible, open = true, onToggle }) {
    return /*#__PURE__*/ _jsxs("section", {
        className: "rounded-xl border border-slate-200 bg-white p-4 shadow-sm",
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                onClick: collapsible ? onToggle : undefined,
                disabled: !collapsible,
                className: "flex w-full items-start gap-2 text-left disabled:cursor-default",
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: "mt-0.5 flex-shrink-0",
                        children: icon
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "min-w-0 flex-1",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-semibold text-slate-900",
                                children: title
                            }),
                            subtitle && /*#__PURE__*/ _jsx("div", {
                                className: "mt-0.5 text-xs text-slate-500",
                                children: subtitle
                            })
                        ]
                    }),
                    collapsible && /*#__PURE__*/ _jsx("span", {
                        className: "text-xs text-slate-400",
                        children: open ? 'Hide' : 'Show'
                    })
                ]
            }),
            (!collapsible || open) && /*#__PURE__*/ _jsx("div", {
                className: "mt-3",
                children: children
            })
        ]
    });
}
function Group({ title, children }) {
    return /*#__PURE__*/ _jsxs("div", {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500",
                children: title
            }),
            children
        ]
    });
}
function Chip({ children, className = '', variant }) {
    const styles = {
        default: 'border-slate-200 bg-slate-50 text-slate-600',
        required: 'border-red-200 bg-red-50 text-red-700',
        optional: 'border-slate-200 bg-slate-50 text-slate-500',
        pdf: 'border-red-300 bg-red-50 text-red-800',
        xlsx: 'border-green-300 bg-green-50 text-green-800',
        docx: 'border-blue-300 bg-blue-50 text-blue-800',
        pptx: 'border-amber-300 bg-amber-50 text-amber-800'
    };
    const style = styles[variant ?? 'default'];
    return /*#__PURE__*/ _jsx("span", {
        className: `rounded-full border px-1.5 py-px text-[10px] font-medium ${style} ${className}`,
        children: children
    });
}
/** Map a file format string to the Chip variant. */ function formatVariant(fmt) {
    const lower = fmt.toLowerCase();
    if (lower === 'pdf') return 'pdf';
    if (lower === 'xlsx' || lower === 'xls') return 'xlsx';
    if (lower === 'docx' || lower === 'doc') return 'docx';
    if (lower === 'pptx' || lower === 'ppt') return 'pptx';
    return 'default';
}
function NoSourceCallout({ volume }) {
    return /*#__PURE__*/ _jsx("div", {
        className: "rounded-xl border border-dashed border-amber-200 bg-amber-50/40 p-4 text-sm text-amber-900",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "flex items-start gap-2",
            children: [
                /*#__PURE__*/ _jsx(AlertCircle, {
                    className: "mt-0.5 h-4 w-4 flex-shrink-0",
                    "aria-hidden": "true"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            className: "font-medium",
                            children: "No source template found"
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "mt-1 text-xs",
                            children: [
                                "The insights pipeline labelled this volume as a ",
                                volume.fileFormat || volume.kind,
                                volume.sourceAttachment ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        ' ',
                                        "referencing ",
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-mono",
                                            children: volume.sourceAttachment
                                        })
                                    ]
                                }) : null,
                                ", but no matching attachment was found in the project files. Upload the gov template via",
                                ' ',
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold",
                                    children: "Sources / Files"
                                }),
                                " and it will appear here on the next refresh."
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
